import * as React from 'react'
import { type FeedsCoverage, type PlantEquipment } from '@axilon-apps/domains-ontology-ui-state'
import { FlowTile, FlowTileCaret } from './FlowTile'
import { EquipmentConnectsFlowDag } from './EquipmentConnectsFlowDag'
import { UnorderedNotice } from './UnorderedNotice'

/**
 * Physical equipment connectivity for `LineDetailPage`, ordered along
 * `Equipment -[:CONNECTS_TO]-> Equipment` (same coverage / DAG rules as
 * `SublineFlow` uses for step FEEDS). Distinct from WIP `FEEDS` and from
 * the flat equipment tile grid.
 */
export interface EquipmentConnectEdge {
  source: string
  target: string
  regime?: string
}

export interface EquipmentConnectsFlowProps {
  /** Equipment in render order (topological unless `coverage` is `'none'`). */
  equipment: PlantEquipment[]
  coverage: FeedsCoverage
  connects?: EquipmentConnectEdge[]
}

const UNORDERED_NOTICE: Record<Exclude<FeedsCoverage, 'full'>, string> = {
  none: 'No CONNECTS_TO relationships exist between this equipment — listed alphabetically, not in physical-connection order.',
  partial:
    'CONNECTS_TO relationships cover only some of this equipment — assets without a connection are shown unconnected.',
  branching:
    'This equipment network branches or merges — assets are shown in a valid connection order, with parallel paths flattened.'
}

export function EquipmentConnectsFlow({
  equipment,
  coverage,
  connects = []
}: EquipmentConnectsFlowProps): React.ReactElement {
  const ids = React.useMemo(() => new Set(equipment.map((eq) => eq.id)), [equipment])
  const relevant = React.useMemo(
    () => connects.filter((e) => ids.has(e.source) && ids.has(e.target) && e.source !== e.target),
    [connects, ids]
  )
  const showDag = (coverage === 'branching' || coverage === 'partial') && relevant.length > 0

  if (showDag) {
    return (
      <div>
        {coverage === 'partial' && <UnorderedNotice>{UNORDERED_NOTICE.partial}</UnorderedNotice>}
        <EquipmentConnectsFlowDag equipment={equipment} connects={relevant} />
      </div>
    )
  }

  const isOrdered = coverage === 'full'
  return (
    <div>
      {coverage !== 'full' && equipment.length > 0 && (
        <UnorderedNotice>{UNORDERED_NOTICE[coverage]}</UnorderedNotice>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        {equipment.map((eq, idx) => (
          <React.Fragment key={eq.id}>
            <FlowTile
              kind="equipment"
              id={eq.id}
              label="Equipment"
              node={eq}
              name={eq.name}
              subtitle={eq.equipment_type}
              equipmentType={eq.equipment_type}
            />
            {isOrdered && idx < equipment.length - 1 && <FlowTileCaret />}
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}
